<template>
    <v-app>
        <div v-if="!isLoadingTerms">
            <template-header />
            <cart-summary-widget />
            <router-view style="min-height: 500px;" />
            <template-footer />
            <cookie-banner :policy-url="`/documents/${terms.lang}/cookies.pdf`" />
        </div>
    </v-app>
</template>

<script setup>
    import { ref, onMounted, inject, computed, watch } from "vue";
    import { useStore } from "vuex";
    import { useRoute } from "vue-router";
    import AOS from "aos";
    import { initializeValidationRules } from "@/validationRules";
    import { updateRouteSeo } from "@/utils/seo";
    import TemplateHeader from "@/components/tpl-header.vue";
    import TemplateFooter from "@/components/tpl-footer.vue";
    import CookieBanner from "@/components/banner-cookie-consent.vue";
    import CartSummaryWidget from "@/components/cart-summary-widget.vue";

    const axios = inject('axios');
    const store = useStore();
    const route = useRoute();
    const terms = computed(() => store.state.terms);
    const isLoadingTerms = ref(true);
    store.commit("setEnv", import.meta.env.VITE_APP_ENV_NAME);

    // Watch for language changes and refresh meta tags dynamically
    watch(() => store.state.languageCode, (newLang) => {
        updateRouteSeo(route, newLang);
    });

    // Fetch terms and initialize validation rules
    const fetchTerms = async () => {
        try {
            const langCode = store.state.languageCode;
            const resp = await axios.get(`/terms/${langCode}`);
            const terms = resp.data.data;
            store.commit("setTerms", terms);

            const rules = initializeValidationRules(terms);
            store.commit("setValidationRules", rules);

            // Update SEO with newly loaded terms
            updateRouteSeo(route, langCode);
        }
        catch (error) {
            console.error("Failed to load meta!");
        }
        finally {
            isLoadingTerms.value = false;
        }
    };

    onMounted(async () => {
        // Preload offers/products in the background so they are ready when navigating to offers
        store.dispatch("fetchProducts", { axios });
        await fetchTerms();
    });
</script>

<style>
    /*APP.VUE*/

    html, body {
        overflow-x: clip !important;
        width: 100% !important;
        max-width: 100% !important;
        margin: 0;
        padding: 0;
    }

    body {
        font-family: "Open Sans", sans-serif;
        color: #444444;
        padding-top: 72px !important;
    }

    @media (min-width: 600px) and (max-width: 959px) {
        body {
            padding-top: 60px !important;
        }
    }

    @media (max-width: 599px) {
        body {
            padding-top: 52px !important;
        }
    }

    a {
        color: #ffc451;
        text-decoration: none;
    }

        a:hover {
            color: #ffd584;
            text-decoration: none;
        }

    h1, h2, h3, h4, h5, h6 {
        font-family: "Raleway", sans-serif;
    }

    /* Disable aos animation delay on mobile devices */
    @media screen and (max-width: 768px) {
        [data-aos-delay] {
            transition-delay: 0s !important;
        }
    }

    .video-element {
        width: 100%;
        max-width: 600px;
        margin-bottom: 20px;
    }

    .page {
        padding-top: 16px;
        padding-bottom: 24px;
    }

    .restricted-width {
        max-width: 1200px !important;
        margin-left: auto !important;
        margin-right: auto !important;
    }

    @keyframes cartBump {
        0% { transform: scale(1); }
        30% { transform: scale(1.35) rotate(-10deg); }
        60% { transform: scale(1.2) rotate(10deg); }
        100% { transform: scale(1) rotate(0deg); }
    }

    .cart-bump-animate {
        animation: cartBump 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
    }

    @keyframes cardShake {
        0% { transform: translateX(0); }
        20% { transform: translateX(-6px); }
        40% { transform: translateX(6px); }
        60% { transform: translateX(-4px); }
        80% { transform: translateX(4px); }
        100% { transform: translateX(0); }
    }

    .card-shake-animate {
        animation: cardShake 0.4s ease-in-out !important;
    }

    /* Sections */
    section {
        padding: 50px 0;
    }

    @media (max-width: 1600px) {
        section {
            padding-left: 30px !important;
            padding-right: 30px !important;
        }
    }

    @media (max-width: 920px) {
        section {
            padding-top: 24px !important;
            padding-bottom: 24px !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
        }
        section + section {
            padding-top: 10px !important;
        }
        .section-title {
            padding-bottom: 20px !important;
        }
    }

    @media (max-width: 480px) {
        section {
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
        }
        section + section {
            padding-top: 6px !important;
        }
        .section-title {
            padding-bottom: 18px !important;
        }
    }

    .section-title {
        padding-bottom: 24px;
        margin-bottom: 0;
    }

        .section-title h1,
        .section-title h2,
        .section-title h4 {
            font-size: 14px;
            font-weight: 500;
            padding: 0;
            line-height: 1px;
            margin: 0 0 5px 0;
            letter-spacing: 2px;
            text-transform: uppercase;
            color: #aaaaaa;
            font-family: "Poppins", sans-serif;
        }

            .section-title h1::after,
            .section-title h2::after,
            .section-title h4::after {
                content: "";
                width: 120px;
                height: 1px;
                display: inline-block;
                background: #ffc451;
                margin: 4px 10px;
            }

        .section-title p {
            margin: 0;
            font-size: 36px;
            font-weight: 700;
            text-transform: uppercase;
            font-family: "Poppins", sans-serif;
            color: #151515;
        }

    @media (max-width: 920px) {
        .section-title h1,
        .section-title h2,
        .section-title h4 {
            font-size: 12px !important;
            letter-spacing: 1.5px !important;
        }
        .section-title h1::after,
        .section-title h2::after,
        .section-title h4::after {
            width: 50px !important;
            margin: 3px 6px !important;
        }
        .section-title p {
            font-size: 22px !important;
        }
    }

    @media (max-width: 480px) {
        .section-title h1,
        .section-title h2,
        .section-title h4 {
            font-size: 11px !important;
            letter-spacing: 1px !important;
        }
        .section-title h1::after,
        .section-title h2::after,
        .section-title h4::after {
            width: 30px !important;
            margin: 2px 4px !important;
        }
        .section-title p {
            font-size: 17px !important;
        }
    }

    /* Ensure section hash targets offset below fixed app header */
    section[id],
    div[id],
    section {
        scroll-margin-top: 76px;
    }

    @media (max-width: 960px) {
        section[id],
        div[id],
        section {
            scroll-margin-top: 64px;
        }
    }

    @media (max-width: 600px) {
        section[id],
        div[id],
        section {
            scroll-margin-top: 56px;
        }
    }

    /* Vuetify overrides */
    .v-checkbox .v-input__details {
        font-size: 0.5rem !important; /* Smaller font size */
        padding-top: 0px !important;
        min-height: 14px !important;
        margin-top: -6px;
    }

    .avem-color {
        color: #ffc451;
    }

    /* Gold Metallic Gradient Pill Buttons - Standardized across all text buttons */
    .order-now-btn,
    .add-to-cart-gold-btn,
    .btn-gold-pill,
    .v-btn.bg-avem {
        background: linear-gradient(90deg, #181206 0%, #3d2d0d 20%, #8c6720 50%, #d89e2c 80%, #f4c253 100%) !important;
        color: #ffffff !important;
        font-weight: 700 !important;
        font-size: 1rem !important;
        border: none !important;
        border-radius: 50px !important;
        cursor: pointer;
        padding: 12px 36px !important;
        min-width: 170px !important;
        min-height: 48px !important;
        transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), filter 0.3s ease !important;
        box-shadow: 0 4px 15px rgba(226, 170, 54, 0.4) !important;
        letter-spacing: 0.3px;
        text-transform: none !important;
        position: relative !important;
        overflow: hidden !important;
        z-index: 1 !important;
    }

    .v-btn.bg-avem .v-btn__content {
        position: relative !important;
        z-index: 3 !important;
    }

    .v-btn.bg-avem .v-btn__overlay {
        display: none !important;
    }

    /* Moving Vertical Light Band Sheen Animation */
    .order-now-btn::before,
    .add-to-cart-gold-btn::before,
    .btn-gold-pill::before,
    .v-btn.bg-avem::before {
        content: "" !important;
        position: absolute !important;
        top: 0 !important;
        left: -120% !important;
        width: 55% !important;
        height: 100% !important;
        background: linear-gradient(
            110deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.2) 20%,
            rgba(255, 255, 255, 0.75) 50%,
            rgba(255, 255, 255, 0.2) 80%,
            rgba(255, 255, 255, 0) 100%
        ) !important;
        transform: skewX(-20deg) !important;
        transition: left 0.75s ease-in-out !important;
        pointer-events: none !important;
        z-index: 2 !important;
        display: block !important;
        opacity: 1 !important;
    }

    .order-now-btn:hover::before,
    .add-to-cart-gold-btn:hover::before,
    .btn-gold-pill:hover::before,
    .v-btn.bg-avem:hover::before {
        left: 145% !important;
    }

    .order-now-btn:hover:not(:disabled),
    .add-to-cart-gold-btn:hover:not(:disabled),
    .btn-gold-pill:hover:not(:disabled),
    .v-btn.bg-avem:hover:not(:disabled) {
        transform: translateY(-2px) scale(1.03) !important;
        box-shadow: 0 8px 24px rgba(226, 170, 54, 0.6) !important;
        filter: brightness(1.08);
    }

    .order-now-btn:active:not(:disabled),
    .add-to-cart-gold-btn:active:not(:disabled),
    .btn-gold-pill:active:not(:disabled),
    .v-btn.bg-avem:active:not(:disabled) {
        transform: translateY(1px) scale(0.98) !important;
    }

    .order-now-btn:disabled,
    .add-to-cart-gold-btn:disabled,
    .btn-gold-pill:disabled,
    .v-btn.bg-avem:disabled {
        opacity: 0.45 !important;
        cursor: not-allowed !important;
        box-shadow: none !important;
        transform: none !important;
    }

    /* Facebook Blue Pill Button with Moving Light Sheen Flash Animation */
    .btn-facebook-pill {
        background: linear-gradient(90deg, #091c47 0%, #0d2c6c 25%, #144f9c 60%, #1877f2 100%) !important;
        color: #ffffff !important;
        font-weight: 700 !important;
        font-size: 0.85rem !important;
        border: none !important;
        border-radius: 50px !important;
        cursor: pointer;
        padding: 6px 18px !important;
        min-width: 105px !important;
        min-height: 32px !important;
        transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), filter 0.3s ease !important;
        box-shadow: 0 4px 12px rgba(24, 119, 242, 0.4) !important;
        letter-spacing: 0.3px;
        text-transform: none !important;
        position: relative !important;
        overflow: hidden !important;
        z-index: 1 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .btn-facebook-pill::before {
        content: "" !important;
        position: absolute !important;
        top: 0 !important;
        left: -120% !important;
        width: 55% !important;
        height: 100% !important;
        background: linear-gradient(
            110deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.2) 20%,
            rgba(255, 255, 255, 0.75) 50%,
            rgba(255, 255, 255, 0.2) 80%,
            rgba(255, 255, 255, 0) 100%
        ) !important;
        transform: skewX(-20deg) !important;
        transition: left 0.75s ease-in-out !important;
        pointer-events: none !important;
        z-index: 2 !important;
        display: block !important;
        opacity: 1 !important;
    }

    .btn-facebook-pill:hover::before {
        left: 145% !important;
    }

    .btn-facebook-pill:hover:not(:disabled) {
        transform: translateY(-2px) scale(1.04) !important;
        box-shadow: 0 6px 18px rgba(24, 119, 242, 0.65) !important;
        filter: brightness(1.1) !important;
    }

    .btn-facebook-pill:active:not(:disabled) {
        transform: translateY(1px) scale(0.98) !important;
    }

    /* Red Remove from Cart Button */
    .btn-remove-cart {
        background: linear-gradient(135deg, #e53935 0%, #b71c1c 100%) !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 50px !important;
        cursor: pointer;
        min-height: 42px !important;
        min-width: 44px !important;
        padding: 8px 12px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease !important;
        box-shadow: 0 3px 8px rgba(229, 57, 53, 0.4) !important;
    }

    .btn-remove-cart:hover {
        transform: translateY(-2px) scale(1.05) !important;
        box-shadow: 0 5px 12px rgba(229, 57, 53, 0.6) !important;
        filter: brightness(1.1) !important;
    }

    .btn-remove-cart:active {
        transform: translateY(1px) scale(0.96) !important;
    }

    /* Stepper Quantity Controls */
    .quantity-stepper {
        border: 1px solid #cbd5e0 !important;
        border-radius: 4px !important;
        overflow: hidden !important;
        background: #ffffff !important;
        display: inline-flex !important;
        align-items: center !important;
    }

    .step-btn {
        width: 28px !important;
        height: 32px !important;
        background: #edf2f7 !important;
        border: none !important;
        font-weight: bold !important;
        font-size: 16px !important;
        cursor: pointer;
        color: #4a5568 !important;
        transition: background 0.2s !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .step-btn:hover:not(:disabled) {
        background: #cbd5e0 !important;
    }

    .step-btn:disabled {
        opacity: 0.4 !important;
        cursor: not-allowed !important;
    }

    .qty-input {
        width: 44px !important;
        height: 32px !important;
        text-align: center !important;
        border: none !important;
        border-left: 1px solid #cbd5e0 !important;
        border-right: 1px solid #cbd5e0 !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        color: #2d3748 !important;
        background: transparent !important;
    }

    .qty-input::-webkit-inner-spin-button,
    .qty-input::-webkit-outer-spin-button {
        -webkit-appearance: none !important;
        appearance: none !important;
        margin: 0 !important;
    }
    .qty-input[type=number] {
        -webkit-appearance: textfield !important;
        -moz-appearance: textfield !important;
        appearance: textfield !important;
    }
</style>
